import React, { useState, useEffect } from 'react';
import { saveStripeApiKey, hasStripeApiKey, removeStripeApiKey } from '../utils/stripeApi';

const StripeSettings = ({ userEmail, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [apiKey, setApiKey] = useState('');
  const [apiKeyVisible, setApiKeyVisible] = useState(false);
  const [apiKeyStatus, setApiKeyStatus] = useState({ loading: true, hasKey: false, connectedAt: null });
  const [apiKeySaving, setApiKeySaving] = useState(false);
  const [apiKeyRemoving, setApiKeyRemoving] = useState(false);
  const [apiKeyError, setApiKeyError] = useState('');
  const [apiKeySuccess, setApiKeySuccess] = useState('');

  const loadAccountInfo = async () => {
    try {
      const hasKey = await hasStripeApiKey(userEmail);
      setApiKeyStatus((prev) => ({
        ...prev,
        loading: false,
        hasKey,
      }));
    } catch (error) {
      console.error('Error loading account info:', error);
      setApiKeyStatus((prev) => ({
        ...prev,
        loading: false,
      }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAccountInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail]);

  const handleSaveApiKey = async () => {
    if (!apiKey.trim()) {
      setApiKeyError('Please enter your Stripe API key.');
      return;
    }
    setApiKeySaving(true);
    setApiKeyError('');
    setApiKeySuccess('');
    try {
      await saveStripeApiKey(userEmail, apiKey.trim());
      const hasKey = await hasStripeApiKey(userEmail);
      setApiKeyStatus((prev) => ({
        ...prev,
        hasKey,
      }));
      setApiKey('');
      setApiKeySuccess('API key saved.');
    } catch (error) {
      setApiKeyError(error?.message || 'Unable to save the API key.');
    } finally {
      setApiKeySaving(false);
    }
  };

  const handleRemoveApiKey = async () => {
    setApiKeyRemoving(true);
    setApiKeyError('');
    setApiKeySuccess('');
    try {
      await removeStripeApiKey(userEmail);
      setApiKeyStatus((prev) => ({
        ...prev,
        hasKey: false,
        connectedAt: null,
      }));
      setApiKeySuccess('API key removed.');
    } catch (error) {
      setApiKeyError(error?.message || 'Unable to remove the API key.');
    } finally {
      setApiKeyRemoving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Stripe Settings</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Stripe API Key Section */}
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Stripe API key</h3>
                <p className="text-sm text-gray-600">
                  Use a restricted key so Inbox Mate can check subscriptions, cancel plans, and issue refunds.
                </p>
              </div>
              <div className="flex items-center gap-2">
                {apiKeyStatus.loading ? (
                  <span className="text-xs text-gray-500">Checking status...</span>
                ) : apiKeyStatus.hasKey ? (
                  <span className="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700">
                    Key connected
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
                    No key saved
                  </span>
                )}
              </div>
            </div>

            <div className="border border-gray-200 rounded-xl p-4 bg-gray-50 space-y-4">
              <div className="rounded-lg bg-gray-900 text-gray-100 p-4 space-y-3">
                <div className="text-sm font-semibold text-white">Step 1: Create a restricted key</div>
                <p className="text-xs text-gray-300">
                  We prefill the key name <span className="text-white font-medium">InboxMate.co</span> and the exact permissions below.
                </p>
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="rounded-full bg-white/10 px-2.5 py-1">Customers: Read</span>
                  <span className="rounded-full bg-white/10 px-2.5 py-1">Subscriptions: Read</span>
                  <span className="rounded-full bg-white/10 px-2.5 py-1">Subscriptions: Write</span>
                  <span className="rounded-full bg-white/10 px-2.5 py-1">Charges: Read</span>
                  <span className="rounded-full bg-white/10 px-2.5 py-1">Products: Read</span>
                  <span className="rounded-full bg-white/10 px-2.5 py-1">Refunds: Write</span>
                </div>
                <a
                  href="https://dashboard.stripe.com/apikeys/create?name=InboxMate.co&permissions%5B%5D=rak_customer_read&permissions%5B%5D=rak_subscription_read&permissions%5B%5D=rak_subscription_write&permissions%5B%5D=rak_charge_read&permissions%5B%5D=rak_product_read&permissions%5B%5D=rak_refund_write"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center rounded-md bg-indigo-500/20 px-3 py-2 text-xs font-semibold text-indigo-100 hover:bg-indigo-500/30"
                >
                  Open Stripe and create key
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Step 2: Paste the key</label>
                <div className="flex items-center gap-3">
                  <input
                    type={apiKeyVisible ? 'text' : 'password'}
                    value={apiKey}
                    onChange={(event) => setApiKey(event.target.value)}
                    placeholder="rk_live_..."
                    className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  />
                  <button
                    type="button"
                    onClick={() => setApiKeyVisible((prev) => !prev)}
                    className="text-sm font-medium text-gray-600 hover:text-gray-800"
                  >
                    {apiKeyVisible ? 'Hide' : 'Show'}
                  </button>
                </div>
                <p className="text-xs text-gray-500">We store this securely and you can remove it anytime.</p>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={handleSaveApiKey}
                  disabled={apiKeySaving}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-indigo-700 disabled:opacity-60"
                >
                  {apiKeySaving ? 'Saving...' : 'Step 3: Save API key'}
                </button>
                {apiKeyStatus.hasKey ? (
                  <button
                    type="button"
                    onClick={handleRemoveApiKey}
                    disabled={apiKeyRemoving}
                    className="text-sm font-medium text-gray-500 hover:text-gray-700"
                  >
                    {apiKeyRemoving ? 'Removing...' : 'Remove key'}
                  </button>
                ) : null}
              </div>

              {apiKeyError ? (
                <div className="text-sm text-red-600">{apiKeyError}</div>
              ) : null}
              {apiKeySuccess ? (
                <div className="text-sm text-green-600">{apiKeySuccess}</div>
              ) : null}
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default StripeSettings;
